<script lang="ts">
import { defineComponent } from 'vue'
export default defineComponent({
  name: 'FastUpload',
})
</script>
<template>
  <el-upload
      ref="uploadRef"
      :action="uploadUrl"
      :headers="uploadHeader"
      :before-upload="beforeUpload"
      :on-success="handleSuccess"
      :on-preview="handlePreview"
      :on-remove="handleRemove"
      :on-change="handleChange"
      :on-error="handleError"
      :before-remove="handleBeforeRemove"
      :auto-upload="false"
      :limit="maxFiles"
      :on-exceed="handleExceed"
      :file-list="innerFileList"
      :show-file-list="showList"
      :multiple="maxFiles > 1"
      :accept="acceptFiles"
      :list-type="listType"
      :drag="isDragType"
  >
    <!-- 头像上传展示 -->
    <template v-if="uploadType === 'avatar'">
      <img v-if="modelValue && maxFiles === 1" :src="getModelUrl" class="avatar" />
      <el-icon v-else class="avatar-uploader-icon">
        <Plus />
      </el-icon>
    </template>

    <!-- 照片墙（多图片）展示 -->
    <template v-else-if="uploadType === 'picture-card'">
      <el-icon class="avatar-uploader-icon">
        <Plus />
      </el-icon>
    </template>

    <!-- 普通文件/拖拽上传展示 -->
    <template v-else>
      <el-icon class="el-upload__icon">
        <UploadFilled />
      </el-icon>
      <div class="el-upload__text">
        {{ placeholder || '点击或拖拽文件到此处上传' }}
      </div>
    </template>

    <!-- 自定义文件列表模板：添加名称编辑 -->
    <template #file="{ file }">
      <div v-if="listType === 'picture-card'" class="custom-file-card">
        <img :src="file.url" alt="图片预览" class="custom-file-img" />
        <div class="custom-file-footer">
          <span class="custom-file-name">{{ file.displayName || file.name }}</span>
          <el-button
              v-if="editName"
              size="mini"
              type="text"
              @click.stop="openNameEditModal(file)">
            编辑名称
          </el-button>
        </div>
      </div>
    </template>

    <template #tip>
      <div v-if="uploadTips" class="el-upload__tip">{{ uploadTips }}</div>
    </template>
  </el-upload>

  <!-- 名称编辑弹窗 -->
  <el-dialog
      v-model="nameEditModalVisible"
      title="编辑图片名称"
      width="300px"
      append-to-body
      destroy-on-close
  >
    <el-form :model="nameEditForm" label-width="80px">
      <el-form-item label="图片名称">
        <el-input
            v-model="nameEditForm.name"
            placeholder="请输入图片展示名称"
            maxlength="50"
            show-word-limit
        />
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button @click="nameEditModalVisible = false">取消</el-button>
      <el-button type="primary" @click="confirmNameEdit">确认</el-button>
    </template>
  </el-dialog>
</template>

<script setup lang="ts" name="FastUpload">
import constant from "@/utils/constant";
import { Plus, UploadFilled } from "@element-plus/icons-vue";
import { ElMessage, genFileId, UploadFile, UploadFiles, UploadProps, UploadRawFile } from "element-plus";
import { computed, defineEmits, defineProps, ref, watch } from "vue";
import cache from "@/utils/cache";

// 图片信息接口（你要的格式）
export interface IPicInfo {
  id?: string;
  name?: string;
  url?: string;
}

// 扩展文件类型
interface CustomUploadFile extends UploadFile {
  displayName?: string;
  rawName?: string;
}

// 组件 Props
const props = defineProps({
  modelValue: {
    type: [String, Array, Object],
    default: "",
  },
  uploadType: {
    type: String,
    default: "file",
    validator: (val: string) => ["avatar", "picture-card", "file"].includes(val),
  },
  clearable: { type: Boolean, default: false },
  placeholder: { type: String, default: "" },
  maxSize: { type: Number, default: 10 },
  maxFiles: { type: Number, default: 1 },
  savePath: { type: String, default: "" },
  fileName: { type: String, default: "" },
  fileType: { type: Array, default: () => ["image/*"] },
  showList: { type: Boolean, default: true },
  uploadTips: { type: String, default: "" },
  uploadUrl: { type: String, default: constant.uploadUrl },
  editName: { type: Boolean, default: false }, // 编辑名称开关
});

const emit = defineEmits(["update:modelValue"]);

// 内部状态
const uploadRef = ref();
const innerFileList = ref<CustomUploadFile[]>([]);
const nameEditModalVisible = ref(false);
const currentEditFile = ref<CustomUploadFile | null>(null);
const nameEditForm = ref({ name: "" });

// 记录父组件传入的 v-model 类型
const parentModelType = ref<"string" | "array" | "object">("string");

watch(
    () => props.modelValue,
    (val) => {
      if (typeof val === "string") {
        parentModelType.value = "string";
      } else if (Array.isArray(val)) {
        parentModelType.value = "array";
      } else if (val && typeof val === "object") {
        parentModelType.value = "object";
      }
    },
    { immediate: true }
);

// 计算属性
const acceptFiles = computed(() => props.fileType.join(","));
const listType = computed(() => (props.uploadType === "avatar" || props.uploadType === "picture-card" ? "picture-card" : "text"));
const isDragType = computed(() => props.uploadType === "file");
const uploadHeader = computed(() => ({ Authorization: cache.getToken() || "" }));

const getModelUrl = computed(() => {
  const val = props.modelValue;
  if (Array.isArray(val) && val.length) return (val[0] as IPicInfo).url || val[0];
  if (typeof val === "object" && val) return (val as IPicInfo).url;
  return val as string;
});

// 初始化回填（兼容所有格式）
watch(
    () => props.modelValue,
    (val) => {
      if (!val) {
        innerFileList.value = [];
        return;
      }

      let list: CustomUploadFile[] = [];

      if (Array.isArray(val)) {
        if (val.length && typeof val[0] === "object") {
          list = (val as IPicInfo[]).map((item, i) => ({
            uid: item.id || Date.now() + i,
            name: item.name || `img_${i + 1}`,
            displayName: item.name,
            url: item.url,
            status: "success",
            rawName: item.name,
          } as CustomUploadFile));
        } else {
          list = (val as string[]).map((url, i) => ({
            uid: Date.now() + i,
            name: `img_${i + 1}`,
            displayName: `img_${i + 1}`,
            url,
            status: "success",
            rawName: `img_${i + 1}`,
          } as CustomUploadFile));
        }
      } else if (typeof val === "object") {
        const item = val as IPicInfo;
        list = [
          {
            uid: item.id || Date.now(),
            name: item.name || "img_1",
            displayName: item.name,
            url: item.url,
            status: "success",
            rawName: item.name,
          } as CustomUploadFile,
        ];
      } else if (typeof val === "string") {
        const urls = val.split(",").filter(Boolean);
        list = urls.map((url, i) => ({
          uid: Date.now() + i,
          name: `img_${i + 1}`,
          displayName: `img_${i + 1}`,
          url,
          status: "success",
          rawName: `img_${i + 1}`,
        } as CustomUploadFile));
      }

      innerFileList.value = list;
    },
    { immediate: true }
);

// 打开编辑名称
const openNameEditModal = (file: CustomUploadFile) => {
  currentEditFile.value = file;
  nameEditForm.value.name = file.displayName || file.name || "";
  nameEditModalVisible.value = true;
};

// 确认编辑
const confirmNameEdit = () => {
  if (!currentEditFile.value || !nameEditForm.value.name.trim()) {
    ElMessage.warning("请输入有效名称");
    return;
  }
  currentEditFile.value.displayName = nameEditForm.value.name.trim();
  currentEditFile.value.name = nameEditForm.value.name.trim();
  updateModelValue();
  nameEditModalVisible.value = false;
  ElMessage.success("修改成功");
};

// ==============================================
// 核心：自动识别父组件类型并返回对应格式（最终修复版）
// ==============================================
const updateModelValue = () => {
  const picList: IPicInfo[] = innerFileList.value
      .filter((item) => item.status === "success" && item.url)
      .map((item, i) => ({
        id: item.uid.toString(),
        name: item.displayName || item.rawName || `img_${i + 1}`,
        url: item.url!,
      }));

  // 单文件
  if (props.maxFiles === 1) {
    const item = picList[0];
    if (parentModelType.value === "string") {
      emit("update:modelValue", item?.url || "");
    } else {
      emit("update:modelValue", item || {});
    }
  }
  // 多文件
  else {
    if (parentModelType.value === "string") {
      const urls = picList.map((i) => i.url).filter(Boolean);
      emit("update:modelValue", urls.join(","));
    } else {
      emit("update:modelValue", picList);
    }
  }
};

// 上传前校验
const beforeUpload: UploadProps["beforeUpload"] = (file) => {
  const max = props.maxSize * 1024 * 1024;
  if (file.size > max) {
    ElMessage.error(`最大支持${props.maxSize}MB`);
    return false;
  }
  if (props.maxFiles === 1) {
    innerFileList.value = [
      {
        uid: file.uid,
        name: file.name,
        rawName: file.name,
        displayName: file.name,
      } as CustomUploadFile,
    ];
  }
  if (props.maxFiles > 1 && innerFileList.value.length >= props.maxFiles) {
    ElMessage.error(`最多上传${props.maxFiles}张`);
    return false;
  }
  return true;
};

// 上传成功
const handleSuccess: UploadProps["onSuccess"] = (res, file) => {
  if (res.code !== 0) {
    ElMessage.error("上传失败");
    return;
  }
  const data = res.data;
  const target = innerFileList.value.find((x) => x.uid === file.uid);
  if (target) {
    target.url = data.url;
    target.status = "success";
  }
  updateModelValue();
  ElMessage.success("上传成功");
};

// 删除
const handleRemove: UploadProps["onRemove"] = (file) => {
  innerFileList.value = innerFileList.value.filter((x) => x.uid !== file.uid);
  updateModelValue();
};

// 自动提交
const handleChange: UploadProps["onChange"] = () => {
  uploadRef.value?.submit();
};

// 错误
const handleError: UploadProps["onError"] = () => ElMessage.error("上传异常");

// 预览
const handlePreview: UploadProps["onPreview"] = (file) => {};
const handleBeforeRemove = () => true;

// 超出数量
const handleExceed: UploadProps["onExceed"] = (files) => {
  console.log("[handleExceed] 替换文件：", files, props.maxFiles)
  if (props.maxFiles === 1 && !props.showList) {
    uploadRef.value!.clearFiles()
    const file = files[0] as UploadRawFile
    file.uid = genFileId()
    uploadRef.value!.handleStart(file)
  } else {
    ElMessage.error(`最多只能上传${props.maxFiles}个文件，请先删除原有文件`);
  }
};
</script>

<style scoped lang="scss">
$size: 130px;

.avatar {
  width: $size;
  height: $size;
  object-fit: cover;
  display: block;
}

.avatar-uploader-icon {
  width: $size;
  height: $size;
  font-size: 28px;
  color: #8c939d;
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.custom-file-card {
  position: relative;
  width: 100%;
  height: 100%;

  .custom-file-img {
    width: 100%;
    height: 160px;
    object-fit: cover;
    border-radius: 6px;
  }

  .custom-file-footer {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 6px;
    background: rgba(0, 0, 0, 0.5);
    color: #fff;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-radius: 0 0 6px 6px;

    .custom-file-name {
      max-width: 110px;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      font-size: 12px;
    }
  }
}
</style>
